import { useState, useCallback, useEffect, useRef } from 'react'

import { useApolloClient } from '@apollo/client'
import { PasswordInput, TextInput } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { RefreshCw } from 'lucide-react'

import { navigate, routes, useParams } from '@redwoodjs/router'
import { useMutation, useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import DnPrompt from 'src/components/Core/DnPrompt'
import CompletionMethodModal from 'src/components/Overlays/CompletionMethodModal/CompletionMethodModal'
import type { CompletionMethodValues } from 'src/components/Overlays/CompletionMethodModal/CompletionMethodModal'
import {
  toMarkVoEAsCompletedInput,
  voeCompletionContext,
} from 'src/components/Overlays/CompletionMethodModal/voeCompletionContext'
import EmailSenderModal from 'src/components/Overlays/EmailSenderModal/EmailSenderModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useAutomatedOutreachEmail } from 'src/components/VoE/AutomatedOutreachPanel/useAutomatedOutreachEmail'
import { useNoContactGuard } from 'src/components/VoE/NoContactGuard/useNoContactGuard'
import { useScheduledOutreachGuard } from 'src/components/VoE/ScheduledOutreachGuard/useScheduledOutreachGuard'
import { useCallDrawerContext } from 'src/context/CallDrawerContext'
import {
  usePhoneDevice,
  usePhoneDeviceDispatch,
} from 'src/context/PhoneDeviceContext'
import { GET_COMPANY } from 'src/graphql/companies.graphql'
import { VoEStatus, VoEType } from 'src/graphql/types/verificationOfEmployments'
import {
  DELETE_VOE,
  GET_VOE_LIST,
  MARK_VOE_AS_COMPLETED,
  SEND_INSTRUCTIONS_EMAIL_FOR_OUTGOING_VOE,
  UPDATE_VOE,
} from 'src/graphql/voe.graphql'
import { buildFullName, formatDateSimple } from 'src/lib/formatters'
import { PhoneDeviceActionType } from 'src/lib/phone-device.reducer'
import {
  formatInternationalPhoneNumber,
  unformatPhoneNumber,
} from 'src/lib/phone.utils'
import { DEFAULT_VOE_VERIFICATION_PERIOD_MONTHS } from 'src/lib/voeVerificationPeriod'

import ContactModal from './components/ContactModal/ContactModal'
import VoEApplicationTable from './components/VoEApplicationTable/VoEApplicationTable'
import VoEDetails from './components/VoEDetails/VoEDetails'
import { buildVoEInstructionsEmailBody } from './lib/voeEmailTemplate'

const LIMIT = 100

// Returns the best available recipient email for a VoE:
const getRecipientEmail = (voe: any): string => {
  if (!voe) return ''
  return voe.email || ''
}

// Helper function to compare contact information from modal with current VoE data
const getContactInformationUpdatesFromModal = (
  contactInfo: any,
  currentVoe: any
) => {
  const updates: any = {}
  let hasUpdates = false

  if (contactInfo.contactName && contactInfo.contactName.trim() !== '') {
    if (contactInfo.contactName !== currentVoe.contactName) {
      updates.contactName = contactInfo.contactName
      hasUpdates = true
    }
  }

  if (contactInfo.email && contactInfo.email.trim() !== '') {
    if (contactInfo.email !== currentVoe.email) {
      updates.email = contactInfo.email
      hasUpdates = true
    }
  }

  if (contactInfo.phone && contactInfo.phone.trim() !== '') {
    if (contactInfo.phone !== currentVoe.phoneNumber) {
      updates.phoneNumber = contactInfo.phone
      hasUpdates = true
    }
  }

  if (contactInfo.fax && contactInfo.fax.trim() !== '') {
    if (contactInfo.fax !== currentVoe.faxNumber) {
      updates.faxNumber = contactInfo.fax
      hasUpdates = true
    }
  }

  return hasUpdates ? updates : null
}

const VoETab = ({ applicantId, applicant }) => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)
  const [selectedVoe, setSelectedVoe] = useState(null)
  const [openEmailModal, { open, close }] = useDisclosure(false)
  const [completionModalOpened, completionModal] = useDisclosure(false)
  const [voeToComplete, setVoeToComplete] = useState(null)
  const { guardContact, noContactPrompt } = useNoContactGuard()
  const { guardScheduledOutreach, scheduledOutreachPrompt } =
    useScheduledOutreachGuard()
  const [isVoEDetailsOpen, setIsVoEDetailsOpen] = useState(false)
  const [selectedVoeForDetails, setSelectedVoeForDetails] = useState(null)
  const [isContactUpdatePromptOpen, setIsContactUpdatePromptOpen] =
    useState(false)
  const [contactUpdateData, setContactUpdateData] = useState(null)

  const { currentUser } = useAuth()
  // Set when another screen links straight to one VoE (e.g. the status badge
  // in Employment History), or when a VoE agent-call notification opens the
  // call tab with the specific call id.
  const { voeId: voeIdFromUrl, activityLogId: activityLogIdFromUrl } =
    useParams()
  const { handleOpen: openCallDrawer } = useCallDrawerContext() ?? {}
  const openedCallIdRef = useRef<string | null>(null)
  const phoneDeviceContext = usePhoneDevice()
  const dispatchPhoneAction = usePhoneDeviceDispatch()

  const { data: companyData } = useQuery(GET_COMPANY, {
    variables: { id: currentUser?.companyId },
    skip: !currentUser?.companyId,
  })
  const verificationPeriodMonths =
    companyData?.company?.voeVerificationPeriodMonths ??
    DEFAULT_VOE_VERIFICATION_PERIOD_MONTHS

  const { data: voeList, loading } = useQuery(GET_VOE_LIST, {
    variables: {
      filters: {
        type: VoEType.OUTBOUND,
        applicantId,
      },
      pageInfo: { offset: 0, limit: LIMIT },
    },
    onError: () => {
      toast('Unable to get VoE list, please try again.', 'error')
    },
  })

  // Update selectedVoeForDetails when voeList changes (after refetch)
  useEffect(() => {
    if (selectedVoeForDetails && voeList?.verificationOfEmployments?.items) {
      const updatedVoe = voeList.verificationOfEmployments.items.find(
        (voe) => voe.voeId === selectedVoeForDetails.voeId
      )
      if (updatedVoe) {
        setSelectedVoeForDetails(updatedVoe)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voeList])

  useEffect(() => {
    if (!voeIdFromUrl) return
    const linkedVoe = voeList?.verificationOfEmployments?.items?.find(
      (voe) => voe.voeId === voeIdFromUrl
    )
    if (linkedVoe) {
      setSelectedVoeForDetails(linkedVoe)
      setIsVoEDetailsOpen(true)
    }
  }, [voeIdFromUrl, voeList])

  useEffect(() => {
    // The notifications drawer stays mounted, so clicking the same call
    // notification is the same URL. Clear the guard once the call params are
    // gone, or that click never reopens the drawer.
    if (!activityLogIdFromUrl) {
      openedCallIdRef.current = null
      return
    }
    if (!openCallDrawer) return
    if (openedCallIdRef.current === activityLogIdFromUrl) return

    // Drawer.Stack keeps the last drawer to open on top. VoE details opens
    // only after the list resolves, so opening the call first lets details
    // cover the call the notification pointed at. Wait until details is open,
    // or until the list has settled without that VoE.
    if (voeIdFromUrl) {
      if (loading) return
      const linkedVoe = voeList?.verificationOfEmployments?.items?.find(
        (voe) => voe.voeId === voeIdFromUrl
      )
      if (linkedVoe && !isVoEDetailsOpen) return
    }

    openedCallIdRef.current = activityLogIdFromUrl
    openCallDrawer(activityLogIdFromUrl, () => {
      const currentUrl = new URL(window.location.href)
      currentUrl.searchParams.delete('activityLogId')
      currentUrl.searchParams.delete('voiceAgentId')
      navigate(`${currentUrl.pathname}${currentUrl.search}`, { replace: true })
    })
  }, [
    activityLogIdFromUrl,
    openCallDrawer,
    voeIdFromUrl,
    loading,
    voeList,
    isVoEDetailsOpen,
  ])

  const refetchQueries = [
    {
      query: GET_VOE_LIST,
      variables: {
        filters: {
          type: VoEType.OUTBOUND,
          applicantId,
        },
        pageInfo: { offset: 0, limit: LIMIT },
      },
    },
  ]

  const [deleteVoe] = useMutation(DELETE_VOE, {
    refetchQueries,
    onCompleted: () => {
      toast('VoE deleted successfully', 'success')
    },
    onError: () => {
      toast('Error deleting VoE', 'error')
    },
  })

  const [sendInstructionsEmailForOutgoingVoE, { loading: loadingSendEmail }] =
    useMutation(SEND_INSTRUCTIONS_EMAIL_FOR_OUTGOING_VOE, {
      refetchQueries,
      onCompleted: () => {
        toast('Email sent', 'success')
        close()
      },
      onError: (error) => {
        toast(error.message, 'error')
      },
    })

  const [markAsCompleted, { loading: loadingMarkComplete }] = useMutation(
    MARK_VOE_AS_COMPLETED,
    {
      refetchQueries,
      onCompleted: () => {
        toast('VoE marked as completed successfully', 'success')
        completionModal.close()
        setVoeToComplete(null)
      },
      onError: (error) => {
        toast(error.message, 'error')
      },
    }
  )

  const [updateVoeStatus, { loading: loadingStatusUpdate }] = useMutation(
    UPDATE_VOE,
    {
      refetchQueries,
      onCompleted: () => {
        toast('VoE status updated successfully', 'success')
      },
      onError: (error) => {
        toast(error.message || 'Error updating VoE status', 'error')
      },
    }
  )

  const handleMarkComplete = (voe) => {
    if (voe.responseContent?.voeResponseContentId) {
      markAsCompleted({
        variables: {
          id: voe.voeId,
          input: { completionMethod: 'DOUBLE_NICKEL_FORM' },
        },
      })
    } else {
      setVoeToComplete(voe)
      completionModal.open()
    }
  }

  const handleStatusChange = (voe, status: VoEStatus) => {
    if (status === VoEStatus.COMPLETED) {
      handleMarkComplete(voe)
      return
    }
    updateVoeStatus({ variables: { id: voe.voeId, input: { status } } })
  }

  const [updateVoeContactInfo] = useMutation(UPDATE_VOE, {
    refetchQueries,
    onCompleted: () => {
      toast('VoE contact information updated successfully', 'success')

      // After updating contact info, check for third contact attempt
      if (contactUpdateData?.voe) {
        checkForThirdContactAttempt(contactUpdateData.voe)
      }

      setIsContactUpdatePromptOpen(false)
      setContactUpdateData(null)
    },
    onError: (error) => {
      toast(error.message, 'error')
    },
  })

  const handleContact = (voe) => {
    handleNoContactPrompt(voe, () => {
      setSelectedVoe(voe)
      setIsContactModalOpen(true)
    })
  }

  const CONTACT_TYPES = ['CALL', 'EMAIL', 'FAX', 'ATS']

  const checkForThirdContactAttempt = (voe) => {
    if (voe && voe.auditTrailLogs && voe.status !== VoEStatus.COMPLETED) {
      const contactAttempts = voe.auditTrailLogs.filter(
        (log) => log.type && CONTACT_TYPES.includes(log.type)
      )

      if (contactAttempts.length >= 3) {
        if (voe.responseContent?.voeResponseContentId) {
          markAsCompleted({
            variables: {
              id: voe.voeId,
              input: { completionMethod: 'DOUBLE_NICKEL_FORM' },
            },
          })
        } else {
          setVoeToComplete(voe)
          completionModal.open()
        }
      }
    }
  }

  const handleContactModalClose = () => {
    setIsContactModalOpen(false)
  }

  const handleContactSuccess = (contactInfo) => {
    if (selectedVoe && contactInfo) {
      // Compare the contact information directly
      const contactUpdates = getContactInformationUpdatesFromModal(
        contactInfo,
        selectedVoe
      )

      if (contactUpdates) {
        setContactUpdateData({
          voe: selectedVoe,
          updates: contactUpdates,
          contactInfo: contactInfo,
        })
        setIsContactUpdatePromptOpen(true)
        return // Don't check for third contact attempt yet, let user decide on updates first
      }
    }

    // If no contact updates needed, check for third contact attempt
    setTimeout(() => {
      if (selectedVoe) {
        // Find the updated VoE from the list to get the latest audit trail
        const updatedVoe = voeList?.verificationOfEmployments.items.find(
          (voe) => voe.voeId === selectedVoe.voeId
        )
        if (updatedVoe) {
          checkForThirdContactAttempt(updatedVoe)
        }
      }
    }, 1000) // Delay to ensure data has been refetched
  }

  const handleCompletionSubmit = (values: CompletionMethodValues) => {
    if (voeToComplete) {
      markAsCompleted({
        variables: {
          id: voeToComplete.voeId,
          input: toMarkVoEAsCompletedInput(values),
        },
      })
    }
  }

  const handleUpdateContactInfo = () => {
    if (contactUpdateData) {
      updateVoeContactInfo({
        variables: {
          id: contactUpdateData.voe.voeId,
          input: contactUpdateData.updates,
        },
      })
    }
  }

  const handleContactUpdateCancel = () => {
    setIsContactUpdatePromptOpen(false)

    // After canceling contact update, check for third contact attempt
    if (contactUpdateData?.voe) {
      checkForThirdContactAttempt(contactUpdateData.voe)
    }

    setContactUpdateData(null)
  }

  const handleNoContactPrompt = guardContact

  const apolloClient = useApolloClient()
  const outreachEmail = useAutomatedOutreachEmail({
    component: 'VoETab',
    onStarted: () => apolloClient.refetchQueries({ include: [GET_VOE_LIST] }),
  })

  // The No Contact guard first, as for every contact action; its Contact
  // anyway is the one confirmation, so nothing after it asks again.
  const handleEmailModal = (voe) =>
    handleNoContactPrompt(voe, () => {
      const openComposer = (target) => {
        setSelectedVoe(target)
        open()
      }
      outreachEmail.requestEmail(
        {
          voeId: voe.voeId,
          email: voe.email,
          companyName: voe.companyName,
          applicantName: applicant ? buildFullName(applicant) : '',
        },
        {
          sendManually: ({ email }) =>
            openComposer(email ? { ...voe, email } : voe),
          notAutomated: () => openComposer(voe),
        }
      )
    })

  const handleCall = (voe) => {
    handleNoContactPrompt(voe, () => {
      if (phoneDeviceContext?.phoneDevice?.isBusy) return
      dispatchPhoneAction({
        type: PhoneDeviceActionType.CALL,
        payload: {
          phone: unformatPhoneNumber(voe.phoneNumber),
          applicantId,
          applicantFullName: `${applicant?.firstName} ${applicant?.lastName}`,
          voeId: voe.voeId,
          companyName: voe.companyName,
          voeEmail: voe.email,
        },
      })
    })
  }

  const handleDelete = (documentId) => {
    deleteVoe({
      variables: {
        id: documentId,
      },
    })
  }

  const onSendEmail = ({ email }) =>
    guardScheduledOutreach(
      {
        voeId: selectedVoe?.voeId,
        kind: 'EMAIL',
        savedEmail: selectedVoe?.email,
        recipient: email,
      },
      {
        manual: () =>
          sendInstructionsEmailForOutgoingVoE({
            variables: {
              emailTo: email,
              voeId: selectedVoe.voeId,
            },
          }),
        onSentNow: close,
      }
    )

  const handleViewDetail = useCallback(
    (voe) => {
      navigate(
        routes.voeResponseDetails({
          id: applicantId,
          tab: 'voe',
          voeId: voe.voeId,
        })
      )
    },
    [applicantId]
  )

  const handleRowClick = useCallback((voe) => {
    setSelectedVoeForDetails(voe)
    setIsVoEDetailsOpen(true)
  }, [])

  const handleCloseVoEDetails = useCallback(() => {
    setIsVoEDetailsOpen(false)
    setSelectedVoeForDetails(null)
    // Drop the linked VoE so a refetch doesn't reopen the drawer.
    if (voeIdFromUrl) {
      navigate(routes.applicantDetails({ id: applicantId, tab: 'voe' }), {
        replace: true,
      })
    }
  }, [applicantId, voeIdFromUrl])
  const companyName = currentUser?.companyName
  const applicantFullName = `${applicant?.firstName} ${applicant?.lastName}`
  const companyPhone =
    formatInternationalPhoneNumber(
      companyData?.company?.companyPhoneNumber ||
        companyData?.company?.phoneNumber
    ) ?? ''
  const emailBody = buildVoEInstructionsEmailBody({
    companyName,
    applicantName: applicantFullName,
    pastEmployerName: selectedVoe?.companyName ?? '',
    recruiterEmail: currentUser?.email,
    companyPhone,
  })

  return (
    <div className="flex h-[90%] flex-col gap-4 overflow-auto">
      <div className="flex flex-row items-center gap-2">
        <div className="flex w-full flex-row items-center gap-2">
          <div className="text-lg font-semibold text-doubleNickel-black">
            Verification of Employment
          </div>
          <div className="ml-auto text-sm font-semibold text-doubleNickel-gray-700">
            Date of Birth:
          </div>
          <TextInput
            w={125}
            variant="unstyled"
            readOnly
            value={
              applicant?.application?.dob
                ? formatDateSimple(applicant.application.dob)
                : 'Not Available'
            }
          />
          <div className="text-sm font-semibold text-doubleNickel-gray-700">
            SSN:
          </div>
          {applicant?.application?.ssn ? (
            <PasswordInput
              w={125}
              variant="unstyled"
              value={applicant.application.ssn}
              readOnly
            />
          ) : (
            <TextInput
              w={125}
              variant="unstyled"
              readOnly
              value={'Not Available'}
            />
          )}
        </div>
      </div>

      <VoEApplicationTable
        voeList={voeList?.verificationOfEmployments.items}
        loading={loading}
        onContact={handleContact}
        onCall={handleCall}
        onEmail={handleEmailModal}
        onViewDetail={handleViewDetail}
        onDelete={handleDelete}
        onRowClick={handleRowClick}
        onStatusChange={handleStatusChange}
        statusUpdating={loadingStatusUpdate || loadingMarkComplete}
        guardContact={guardContact}
        verificationPeriodMonths={verificationPeriodMonths}
      />

      {isContactModalOpen && (
        <ContactModal
          voe={selectedVoe}
          isOpen={isContactModalOpen}
          onRender={refetchQueries}
          onClose={handleContactModalClose}
          onContactSuccess={handleContactSuccess}
        />
      )}

      {outreachEmail.panel}
      {scheduledOutreachPrompt}

      {openEmailModal && (
        <EmailSenderModal
          from={currentUser?.email}
          to={getRecipientEmail(selectedVoe)}
          title="Send instructions via Email"
          subject={`Employment verification request from ${companyName}`}
          body={emailBody}
          isOpen={openEmailModal}
          onClose={close}
          onSend={onSendEmail}
          loading={loadingSendEmail}
        />
      )}

      <CompletionMethodModal
        opened={completionModalOpened}
        onClose={() => {
          completionModal.close()
          setVoeToComplete(null)
        }}
        onSubmit={handleCompletionSubmit}
        loading={loadingMarkComplete}
        {...voeCompletionContext(
          voeToComplete && { applicant, ...voeToComplete }
        )}
      />

      {noContactPrompt}

      <DnPrompt
        opened={isContactUpdatePromptOpen}
        close={handleContactUpdateCancel}
        title="Update Contact Information"
        description={
          contactUpdateData
            ? `The contact information you used differs from what's currently saved. Would you like to update the VoE contact information with the following changes?${Object.entries(
                contactUpdateData.updates
              )
                .map(([key, value]) => {
                  const fieldName =
                    key === 'phoneNumber'
                      ? 'Phone'
                      : key === 'faxNumber'
                      ? 'Fax'
                      : key === 'contactName'
                      ? 'Contact Name'
                      : 'Email'
                  return `\n• ${fieldName}: ${value}`
                })
                .join('')}`
            : ''
        }
        confirmText="Update contact info"
        cancelText="Keep current info"
        variant="info"
        icon={<RefreshCw size={24} />}
        onConfirm={handleUpdateContactInfo}
        onCancel={handleContactUpdateCancel}
      />

      <VoEDetails
        voe={selectedVoeForDetails}
        opened={isVoEDetailsOpen}
        onClose={handleCloseVoEDetails}
        onContact={handleContact}
        onViewDetail={handleViewDetail}
        onRender={refetchQueries}
        applicantId={applicantId}
        companyPhoneNumber={companyPhone}
        onMarkComplete={handleMarkComplete}
        verificationPeriodMonths={verificationPeriodMonths}
      />
    </div>
  )
}

export default VoETab
